import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  RefreshCw, 
  Eye, 
  EyeOff, 
  ExternalLink,
  ShoppingCart,
  AlertCircle,
  CheckCircle,
  Loader2
} from 'lucide-react';

interface AmazonProduct {
  id: string;
  asin: string;
  title: string;
  description?: string;
  price?: string;
  imageUrl?: string;
  affiliateUrl?: string;
  category?: string;
  brand?: string;
  features?: string[];
  isActive: boolean;
  lastUpdated: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

interface AmazonProductsManagerProps {
  onProductSelect?: (product: AmazonProduct) => void;
  showSelectButton?: boolean;
  className?: string;
  onProductsChange?: () => void; // Callback para notificar cambios
  relatedAdProducts?: string[]; // Array de IDs de productos relacionados
  onRelatedProductsChange?: (productIds: string[]) => void; // Callback para actualizar related_ad_products
  statisticId?: string; // ID de la estadística para actualizar en BD
}

export function AmazonProductsManager({ 
  onProductSelect, 
  showSelectButton = false,
  className = '',
  onProductsChange,
  relatedAdProducts = [],
  onRelatedProductsChange,
  statisticId
}: AmazonProductsManagerProps) {
  const [products, setProducts] = useState<AmazonProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<AmazonProduct | null>(null);
  const [showAllProducts, setShowAllProducts] = useState(false); // Cambiado el nombre para mayor claridad
  const [formData, setFormData] = useState({
    asin: '',
    title: '',
    description: '',
    price: '',
    imageUrl: '',
    category: '',
    brand: '',
    features: [] as string[],
    isActive: true
  });

  // Load products when component mounts or relatedAdProducts changes
  useEffect(() => {
    loadProducts();
  }, [relatedAdProducts]); // Re-cargar cuando cambien los productos relacionados

  const loadProducts = async () => {
    try {
      setLoading(true);
      
      // Si hay productos relacionados, cargar solo esos
      if (relatedAdProducts.length > 0) {
        // console.log('🛒 AMAZON MANAGER: Loading only related products:', relatedAdProducts.length); // Removed for production
        
        const response = await fetch('/api/amazon/products/by-ids', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            ids: relatedAdProducts
          })
        });
        
        if (response.ok) {
          const data = await response.json();
          setProducts(data.products || []);
          // console.log('🛒 AMAZON MANAGER: Loaded related products:', data.products?.length || 0); // Removed for production
        } else {
          // console.log('🛒 AMAZON MANAGER: Failed to load related products, falling back to all'); // Removed for production
          // Fallback: cargar todos los productos
          await loadAllProducts();
        }
      } else {
        // Si no hay productos relacionados, cargar todos
        await loadAllProducts();
      }
    } catch (error) {
      console.error('Error loading products:', error);
      // Fallback: cargar todos los productos
      await loadAllProducts();
    } finally {
      setLoading(false);
    }
  };

  const loadAllProducts = async () => {
    try {
      // console.log('🛒 AMAZON MANAGER: Loading all active products'); // Removed for production
      const response = await fetch('/api/amazon/products');
      const data = await response.json();
      setProducts(data.products || []);
      // console.log('🛒 AMAZON MANAGER: Loaded all products:', data.products?.length || 0); // Removed for production
    } catch (error) {
      console.error('Error loading all products:', error);
    }
  };

  // Filtrar productos para mostrar solo los relacionados con el artículo
  const getRelatedProducts = () => {
    if (relatedAdProducts.length === 0) return [];
    return products.filter(product => relatedAdProducts.includes(product.id));
  };

  // Función para actualizar la base de datos con relatedAdProducts
  const updateDatabaseRelatedProducts = async (newRelatedProducts: string[]) => {
    if (statisticId) {
      try {
        // console.log('🛒 AMAZON MANAGER: Updating database with relatedAdProducts:', newRelatedProducts); // Removed for production
        // console.log('🛒 AMAZON MANAGER: newRelatedProducts type:', typeof newRelatedProducts); // Removed for production
        // console.log('🛒 AMAZON MANAGER: newRelatedProducts length:', newRelatedProducts.length); // Removed for production
        // console.log('🛒 AMAZON MANAGER: newRelatedProducts content:', JSON.stringify(newRelatedProducts)); // Removed for production
        
        const payload = {
          relatedAdProducts: newRelatedProducts
        };
        
        // console.log('🛒 AMAZON MANAGER: Full payload:', JSON.stringify(payload)); // Removed for production
        
        const response = await fetch(`/api/statistics/${statisticId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
        
        if (response.ok) {
          const result = await response.json();
          // console.log('✅ AMAZON MANAGER: Successfully updated database with relatedAdProducts'); // Removed for production
          // console.log('✅ AMAZON MANAGER: Server response:', result); // Removed for production
        } else {
          console.error('❌ AMAZON MANAGER: Failed to update database with relatedAdProducts');
          const errorText = await response.text();
          console.error('❌ AMAZON MANAGER: Error response:', errorText);
        }
      } catch (error) {
        console.error('❌ AMAZON MANAGER: Error updating database:', error);
      }
    } else {
      // console.log('⚠️ AMAZON MANAGER: No statisticId available:', statisticId); // Removed for production
    }
  };

  // Agregar producto al artículo
  const addProductToArticle = async (productId: string) => {
    if (!relatedAdProducts.includes(productId)) {
      const newRelatedProducts = [...relatedAdProducts, productId];
      
      // Actualizar en memoria
      onRelatedProductsChange?.(newRelatedProducts);
      
      // Actualizar en base de datos
      await updateDatabaseRelatedProducts(newRelatedProducts);
    }
  };

  // Remover producto del artículo
  const removeProductFromArticle = async (productId: string) => {
    const newRelatedProducts = relatedAdProducts.filter(id => id !== productId);
    
    // Actualizar en memoria
    onRelatedProductsChange?.(newRelatedProducts);
    
    // Actualizar en base de datos
    await updateDatabaseRelatedProducts(newRelatedProducts);
  };

  // Filtrar productos según el modo de visualización
  const getDisplayProducts = () => {
    let displayProducts = products;
    
    // Por defecto, mostrar solo productos relacionados con este artículo
    // (solo si hay productos relacionados)
    if (relatedAdProducts.length > 0) {
      displayProducts = displayProducts.filter(product => 
        relatedAdProducts.includes(product.id)
      );
    }
    
    // Aplicar filtro de búsqueda
    if (searchQuery.trim()) {
      displayProducts = displayProducts.filter(product => 
        product.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.asin.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.category?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    // Si estamos en modo "ver todos" (toggle activado)
    if (showAllProducts) {
      // Mostrar todos los productos
      displayProducts = products;
    }
    
    return displayProducts;
  };

  // Función para agregar automáticamente un producto al artículo cuando se crea
  const handleProductCreated = async (newProduct: any) => {
    // console.log('🛒 AMAZON MANAGER: handleProductCreated called with:', newProduct); // Removed for production
    // console.log('🛒 AMAZON MANAGER: Product ID:', newProduct?.id); // Removed for production
    // console.log('🛒 AMAZON MANAGER: Product title:', newProduct?.title); // Removed for production
    
    if (!newProduct || !newProduct.id) {
      console.error('❌ AMAZON MANAGER: Invalid product data received:', newProduct);
      return;
    }
    
    // console.log('🛒 AMAZON MANAGER: Product created, adding to article:', newProduct.id); // Removed for production
    
    // Agregar el nuevo producto al artículo automáticamente
    if (!relatedAdProducts.includes(newProduct.id)) {
      const newRelatedProducts = [...relatedAdProducts, newProduct.id];
      
      // Actualizar en memoria
      onRelatedProductsChange?.(newRelatedProducts);
      // console.log('🛒 AMAZON MANAGER: Added new product to article in memory:', newProduct.id); // Removed for production
      
      // Actualizar en base de datos si tenemos el ID de la estadística
      if (statisticId) {
        try {
          // console.log('🛒 AMAZON MANAGER: Updating database with new relatedAdProducts:', newRelatedProducts); // Removed for production
          
          const response = await fetch(`/api/statistics/${statisticId}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              relatedAdProducts: newRelatedProducts
            })
          });
          
          if (response.ok) {
            // console.log('✅ AMAZON MANAGER: Successfully updated database with relatedAdProducts'); // Removed for production
          } else {
            console.error('❌ AMAZON MANAGER: Failed to update database with relatedAdProducts');
          }
        } catch (error) {
          console.error('❌ AMAZON MANAGER: Error updating database:', error);
        }
      } else {
        // console.log('⚠️ AMAZON MANAGER: No statisticId provided, cannot update database automatically'); // Removed for production
      }
    } else {
      // console.log('🛒 AMAZON MANAGER: Product already in article, skipping'); // Removed for production
    }
  };

  const displayProducts = getDisplayProducts();

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      loadProducts();
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`/api/amazon/products/search?query=${encodeURIComponent(searchQuery)}`);
      const data = await response.json();
      setProducts(data.products || []);
    } catch (error) {
      console.error('Error searching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProduct = async () => {
    try {
      const response = await fetch('/api/amazon/products', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const responseData = await response.json();
        const newProduct = responseData.product; // El producto está dentro de { product }
        
        // console.log('🛒 AMAZON MANAGER: Product created response:', responseData); // Removed for production
        // console.log('🛒 AMAZON MANAGER: Extracted product:', newProduct); // Removed for production
        
        await loadProducts();
        setShowCreateModal(false);
        resetForm();
        
        // Agregar automáticamente el nuevo producto al artículo
        if (newProduct && newProduct.id) {
          handleProductCreated(newProduct);
        } else {
          console.error('❌ AMAZON MANAGER: Product created but no ID found:', newProduct);
        }
        
        // Notificar cambio a componentes padre
        onProductsChange?.();
      } else {
        const error = await response.json();
        alert(`Error: ${error.message}`);
      }
    } catch (error) {
      console.error('Error creating product:', error);
      alert('Error creating product');
    }
  };

  const handleUpdateProduct = async () => {
    if (!editingProduct) return;

    try {
      const response = await fetch(`/api/amazon/products/${editingProduct.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        await loadProducts();
        setEditingProduct(null);
        resetForm();
        // Notificar cambio a componentes padre
        onProductsChange?.();
      } else {
        const error = await response.json();
        alert(`Error: ${error.message}`);
      }
    } catch (error) {
      console.error('Error updating product:', error);
      alert('Error updating product');
    }
  };

  const handleDeleteProduct = async (id: string) => {
    if (!confirm('¿Estás seguro de que quieres eliminar este producto?')) return;

    try {
      const response = await fetch(`/api/amazon/products/${id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        await loadProducts();
        // Notificar cambio a componentes padre
        onProductsChange?.();
      } else {
        const error = await response.json();
        alert(`Error: ${error.message}`);
      }
    } catch (error) {
      console.error('Error deleting product:', error);
      alert('Error deleting product');
    }
  };

  const handleToggleStatus = async (id: string) => {
    try {
      const response = await fetch(`/api/amazon/products/${id}/toggle`, {
        method: 'POST'
      });

      if (response.ok) {
        await loadProducts();
        // Notificar cambio a componentes padre
        onProductsChange?.();
      } else {
        const error = await response.json();
        alert(`Error: ${error.message}`);
      }
    } catch (error) {
      console.error('Error toggling product status:', error);
      alert('Error toggling product status');
    }
  };

  const handleRefreshProduct = async (id: string) => {
    try {
      const response = await fetch(`/api/amazon/products/${id}/refresh`, {
        method: 'POST'
      });

      if (response.ok) {
        await loadProducts();
        // Notificar cambio a componentes padre
        onProductsChange?.();
      } else {
        const error = await response.json();
        alert(`Error: ${error.message}`);
      }
    } catch (error) {
      console.error('Error refreshing product:', error);
      alert('Error refreshing product');
    }
  };

  const handleLookupAsin = async () => {
    if (!formData.asin.trim()) return;

    // console.log('🔍 AMAZON MANAGER: Starting ASIN lookup for:', formData.asin); // Removed for production

    try {
      // console.log('📤 AMAZON MANAGER: Making request to /api/amazon/products/lookup'); // Removed for production
      
      const response = await fetch('/api/amazon/products/lookup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ asin: formData.asin })
      });

      // console.log('📥 AMAZON MANAGER: Response status:', response.status); // Removed for production
      // console.log('📥 AMAZON MANAGER: Response headers:', Object.fromEntries(response.headers.entries())); // Removed for production

      if (response.ok) {
        const responseText = await response.text();
        // console.log('📥 AMAZON MANAGER: Raw response text:', responseText); // Removed for production
        
        try {
          const data = JSON.parse(responseText);
          // console.log('✅ AMAZON MANAGER: Parsed response data:', data); // Removed for production
          
          const product = data.product;
          // console.log('🛒 AMAZON MANAGER: Product object:', product); // Removed for production
          
          if (product) {
            // console.log('🛒 AMAZON MANAGER: Product details:'); // Removed for production
            // console.log('  - ASIN:', product.asin); // Removed for production
            // console.log('  - Title:', product.title); // Removed for production
            // console.log('  - Price:', product.price); // Removed for production
            // console.log('  - Brand:', product.brand); // Removed for production
            // console.log('  - Image URL:', product.imageUrl); // Removed for production
            // console.log('  - Detail Page URL:', product.detailPageUrl); // Removed for production
            // console.log('  - Features:', product.features); // Removed for production
            // console.log('  - Category:', product.category); // Removed for production
            // console.log('  - Description:', product.description); // Removed for production
            
            const newFormData = {
              ...formData,
              title: product.title || formData.title,
              description: product.description || formData.description,
              price: product.price || formData.price,
              imageUrl: product.imageUrl || formData.imageUrl,
              category: product.category || formData.category,
              brand: product.brand || formData.brand,
              features: product.features || formData.features
            };
            
            // console.log('📝 AMAZON MANAGER: New form data:', newFormData); // Removed for production
            
            setFormData(newFormData);
            // console.log('✅ AMAZON MANAGER: Form data updated successfully'); // Removed for production
          } else {
            // console.log('❌ AMAZON MANAGER: No product found in response'); // Removed for production
            alert('No se encontró el producto en la respuesta');
          }
        } catch (parseError) {
          console.error('❌ AMAZON MANAGER: JSON parse error:', parseError);
          // console.log('❌ AMAZON MANAGER: Raw response that failed to parse:', responseText); // Removed for production
          alert('Error al procesar la respuesta del servidor');
        }
      } else {
        // console.log('❌ AMAZON MANAGER: Response not ok:', response.status, response.statusText); // Removed for production
        const errorText = await response.text();
        // console.log('❌ AMAZON MANAGER: Error response body:', errorText); // Removed for production
        alert('No se pudo encontrar el producto con ese ASIN');
      }
    } catch (error) {
      console.error('❌ AMAZON MANAGER: Network error:', error);
      alert('Error de conexión al buscar el producto');
    }
  };

  const resetForm = () => {
    setFormData({
      asin: '',
      title: '',
      description: '',
      price: '',
      imageUrl: '',
      category: '',
      brand: '',
      features: [],
      isActive: true
    });
  };

  const openEditModal = (product: AmazonProduct) => {
    setEditingProduct(product);
    setFormData({
      asin: product.asin,
      title: product.title,
      description: product.description || '',
      price: product.price || '',
      imageUrl: product.imageUrl || '',
      category: product.category || '',
      brand: product.brand || '',
      features: product.features || [],
      isActive: product.isActive
    });
  };

  const closeModal = () => {
    setShowCreateModal(false);
    setEditingProduct(null);
    resetForm();
  };

  const filteredProducts = products.filter(product =>
    product.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    product.asin.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (product.category && product.category.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className={`amazon-products-manager ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Productos de Amazon</h2>
          <p className="text-muted-foreground">Gestiona los productos de afiliados de Amazon</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
        >
          <Plus className="h-4 w-4" />
          <span>Nuevo Producto</span>
        </button>
      </div>

      {/* Toggle to show all products when needed */}
      {onRelatedProductsChange && (
        <div className="flex justify-end mb-4">
          <button
            onClick={() => {
              setShowAllProducts(!showAllProducts);
              if (!showAllProducts) {
                // Si vamos a mostrar todos, cargar todos los productos
                loadAllProducts();
              } else {
                // Si vamos a mostrar solo relacionados, recargar productos relacionados
                loadProducts();
              }
            }}
            className={`px-3 py-1 rounded-md text-sm transition-colors ${
              showAllProducts 
                ? 'bg-orange-600 text-white' 
                : 'bg-orange-200 text-orange-800 hover:bg-orange-300'
            }`}
          >
            {showAllProducts ? 'Ver Solo Relacionados' : 'Ver Todos los Productos'}
          </button>
        </div>
      )}

      {/* Search */}
      <div className="flex items-center space-x-4 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Buscar productos por título, ASIN o categoría..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            className="w-full pl-10 pr-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          />
        </div>
        <button
          onClick={handleSearch}
          className="bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg transition-colors"
        >
          Buscar
        </button>
      </div>

      {/* Products List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
          <span className="ml-2 text-muted-foreground">Cargando productos...</span>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayProducts.map((product) => (
            <div
              key={product.id}
              className={`bg-card border border-border rounded-lg overflow-hidden hover:shadow-lg transition-all duration-300 ${
                !product.isActive ? 'opacity-60' : ''
              }`}
            >
              {/* Product Image */}
              <div className="aspect-square bg-gray-100 dark:bg-gray-800 relative">
                {product.imageUrl ? (
                  <img
                    src={product.imageUrl}
                    alt={product.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ShoppingCart className="h-12 w-12 text-gray-400" />
                  </div>
                )}
                
                {/* Status Badge */}
                <div className="absolute top-2 right-2">
                  {product.isActive ? (
                    <div className="bg-green-500 text-white text-xs px-2 py-1 rounded-full flex items-center space-x-1">
                      <CheckCircle className="h-3 w-3" />
                      <span>Activo</span>
                    </div>
                  ) : (
                    <div className="bg-red-500 text-white text-xs px-2 py-1 rounded-full flex items-center space-x-1">
                      <AlertCircle className="h-3 w-3" />
                      <span>Inactivo</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Product Info */}
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-sm font-medium text-foreground line-clamp-2 flex-1">
                    {product.title}
                  </h3>
                  <span className="text-xs text-muted-foreground ml-2">
                    {product.asin}
                  </span>
                </div>
                
                {product.price && (
                  <div className="text-lg font-bold text-orange-600 mb-2">
                    {product.price}
                  </div>
                )}

                {product.category && (
                  <div className="text-xs text-muted-foreground mb-2">
                    Categoría: {product.category}
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center justify-between pt-3 border-t border-border">
                  <div className="flex items-center space-x-2">
                    {/* Botones de gestión de productos relacionados */}
                    {onRelatedProductsChange && (
                      <>
                        {relatedAdProducts.includes(product.id) ? (
                          <button
                            onClick={() => removeProductFromArticle(product.id)}
                            className="px-2 py-1 bg-red-100 text-red-600 rounded text-xs hover:bg-red-200 transition-colors"
                            title="Remover del artículo"
                          >
                            Remover
                          </button>
                        ) : (
                          <button
                            onClick={() => addProductToArticle(product.id)}
                            className="px-2 py-1 bg-green-100 text-green-600 rounded text-xs hover:bg-green-200 transition-colors"
                            title="Agregar al artículo"
                          >
                            Agregar
                          </button>
                        )}
                      </>
                    )}
                    
                    {/* Botones de gestión del producto */}
                    <button
                      onClick={() => openEditModal(product)}
                      className="p-1 text-muted-foreground hover:text-primary transition-colors"
                      title="Editar"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleToggleStatus(product.id)}
                      className="p-1 text-muted-foreground hover:text-primary transition-colors"
                      title={product.isActive ? 'Desactivar' : 'Activar'}
                    >
                      {product.isActive ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                    <button
                      onClick={() => handleRefreshProduct(product.id)}
                      className="p-1 text-muted-foreground hover:text-primary transition-colors"
                      title="Actualizar desde Amazon"
                    >
                      <RefreshCw className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteProduct(product.id)}
                      className="p-1 text-muted-foreground hover:text-red-500 transition-colors"
                      title="Eliminar"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  
                  {showSelectButton && onProductSelect && (
                    <button
                      onClick={() => onProductSelect(product)}
                      className="bg-orange-500 hover:bg-orange-600 text-white text-xs px-3 py-1 rounded transition-colors"
                    >
                      Seleccionar
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || editingProduct) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-foreground">
                {editingProduct ? 'Editar Producto' : 'Nuevo Producto'}
              </h3>
              <button
                onClick={closeModal}
                className="text-muted-foreground hover:text-foreground"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              {/* ASIN */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  ASIN *
                </label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={formData.asin}
                    onChange={(e) => setFormData(prev => ({ ...prev, asin: e.target.value }))}
                    placeholder="B07XYZ1234"
                    className="flex-1 px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    maxLength={10}
                  />
                  <button
                    onClick={handleLookupAsin}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
                    disabled={!formData.asin.trim()}
                  >
                    Buscar
                  </button>
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Título *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Título del producto"
                  className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Descripción
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Descripción del producto"
                  rows={3}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>

              {/* Price */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Precio
                </label>
                <input
                  type="text"
                  value={formData.price}
                  onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                  placeholder="$29.99"
                  className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>

              {/* Image URL */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  URL de Imagen
                </label>
                <input
                  type="url"
                  value={formData.imageUrl}
                  onChange={(e) => setFormData(prev => ({ ...prev, imageUrl: e.target.value }))}
                  placeholder="https://example.com/image.jpg"
                  className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Categoría
                </label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  placeholder="Electronics, Books, etc."
                  className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>

              {/* Brand */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Marca
                </label>
                <input
                  type="text"
                  value={formData.brand}
                  onChange={(e) => setFormData(prev => ({ ...prev, brand: e.target.value }))}
                  placeholder="Apple, Samsung, etc."
                  className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>

              {/* Active Status */}
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                  className="rounded border-border"
                />
                <label htmlFor="isActive" className="text-sm font-medium text-foreground">
                  Producto activo
                </label>
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex items-center justify-end space-x-4 mt-6 pt-4 border-t border-border">
              <button
                onClick={closeModal}
                className="px-4 py-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={editingProduct ? handleUpdateProduct : handleCreateProduct}
                className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg transition-colors"
                disabled={!formData.asin.trim() || !formData.title.trim()}
              >
                {editingProduct ? 'Actualizar' : 'Crear'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AmazonProductsManager;
